import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Clock, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { Event } from '../../../domain/entities/Event';
import { GetUpcomingEventsUseCase } from '../../../application/use-cases/GetUpcomingEventsUseCase';
import { GoogleSheetsEventRepositoryImpl } from '../../../infrastructure/repositories/GoogleSheetsEventRepositoryImpl';
import calendarioImg from '../../../assets/CasadoPandeiro_calendario.jpg';

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function compareEventsChronologically(a: Event, b: Event): number {
  const dateDiff = a.date.value.getTime() - b.date.value.getTime();
  if (dateDiff !== 0) return dateDiff;
  return a.time.value.localeCompare(b.time.value);
}

function EventDetailCard({ event }: { event: Event }) {
  return (
    <div className="p-5 bg-card rounded-lg border hover:shadow-md transition-shadow">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-accent mb-1 block">Evento</span>
      <h4 className="text-base text-primary mb-1">{event.title.value}</h4>
      {event.subtitle.value && (
        <p className="text-sm text-muted-foreground mb-2">{event.subtitle.value}</p>
      )}
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Clock size={14} className="text-accent" />
          {event.time.value}{event.endTime ? ` – ${event.endTime.value}` : ''}
        </span>
        {event.instructor.value && (
          <span className="flex items-center gap-1.5">
            <MapPin size={14} className="text-accent" />
            {event.instructor.value}
          </span>
        )}
      </div>
    </div>
  );
}

export const CalendarioPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const eventsData = await new GetUpcomingEventsUseCase(
          new GoogleSheetsEventRepositoryImpl()
        ).execute();
        setEvents(eventsData);
      } catch (error) {
        console.error('Erro ao carregar calendário:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

  const eventosDoMes = useMemo(
    () => events.filter((e) => e.date.value.getMonth() === currentMonth && e.date.value.getFullYear() === currentYear),
    [events, currentMonth, currentYear]
  );

  const eventosLista = useMemo(
    () => eventosDoMes.filter((e) => e.isUpcoming()).sort(compareEventsChronologically),
    [eventosDoMes]
  );

  const eventosDoDia = useMemo(
    () => (selectedDate
      ? events.filter((e) => isSameDay(e.date.value, selectedDate)).sort(compareEventsChronologically)
      : []),
    [events, selectedDate]
  );

  function prev() {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear((y) => y - 1); }
    else setCurrentMonth((m) => m - 1);
    setSelectedDate(null);
  }

  function next() {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear((y) => y + 1); }
    else setCurrentMonth((m) => m + 1);
    setSelectedDate(null);
  }

  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  function getEventsForDay(day: number) {
    return eventosDoMes
      .filter((e) => e.date.value.getDate() === day)
      .sort(compareEventsChronologically);
  }

  function isToday(day: number) {
    return day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
  }

  function isSelected(day: number) {
    return !!(selectedDate && isSameDay(selectedDate, new Date(currentYear, currentMonth, day)));
  }

  return (
    <div>
      <div
        className="w-full"
        style={{
          backgroundImage: `url(${calendarioImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        role="img"
        aria-label="Calendário Casa do Pandeiro"
      >
        <div className="container py-40 md:py-56" />
      </div>

      <div className="container pt-8 pb-16 md:pt-12 md:pb-20 max-w-5xl mx-auto">
        <h1 className="text-3xl md:text-4xl text-primary mb-3">Calendário</h1>
        <p className="text-muted-foreground mb-10">O que está acontecendo na Casa do Pandeiro</p>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Calendar grid */}
            <div className="bg-card rounded-lg border p-4 md:p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <button onClick={prev} className="p-2 rounded-md hover:bg-secondary transition-colors" aria-label="Mês anterior">
                  <ChevronLeft size={20} />
                </button>
                <h2 className="text-lg md:text-xl font-semibold text-foreground">
                  {MESES[currentMonth]} {currentYear}
                </h2>
                <button onClick={next} className="p-2 rounded-md hover:bg-secondary transition-colors" aria-label="Próximo mês">
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="grid grid-cols-7 mb-2">
                {DIAS_SEMANA.map((d) => (
                  <div key={d} className="text-center text-xs font-medium text-muted-foreground py-2">{d}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-px bg-border">
                {days.map((day, i) => {
                  if (day === null) {
                    return <div key={i} className="invisible bg-card min-h-[60px] md:min-h-[90px]" />;
                  }
                  const evs = getEventsForDay(day);
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedDate(new Date(currentYear, currentMonth, day))}
                      className={`
                        flex flex-col items-start p-1 md:p-2 min-h-[60px] md:min-h-[90px] text-sm transition-colors bg-card hover:bg-secondary/50 cursor-pointer
                        ${isToday(day) ? 'ring-2 ring-inset ring-accent' : ''}
                        ${isSelected(day) ? 'bg-primary/10' : ''}
                      `}
                    >
                      <span className={`text-xs md:text-sm mb-1 ${isToday(day) ? 'font-bold text-accent' : 'text-foreground'}`}>
                        {day}
                      </span>
                      {evs.slice(0, 2).map((event, j) => (
                        <span
                          key={j}
                          className="block w-full text-[9px] md:text-[11px] leading-tight truncate rounded px-1 py-0.5 mb-0.5 font-medium bg-accent/20 text-accent"
                        >
                          {event.title.value}
                        </span>
                      ))}
                      {evs.length > 2 && (
                        <span className="text-[9px] text-muted-foreground">+{evs.length - 2} mais</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected day detail */}
            {selectedDate && (
              <div className="mb-8">
                <h3 className="text-lg text-foreground font-medium mb-4">
                  {selectedDate.getDate()} de {MESES[selectedDate.getMonth()].toLowerCase()} — {DIAS_SEMANA[selectedDate.getDay()]}
                </h3>
                {eventosDoDia.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum evento neste dia.</p>
                ) : (
                  <div className="space-y-3">
                    {eventosDoDia.map((event) => (
                      <EventDetailCard key={event.id.value} event={event} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Events this month */}
            <div className="mb-10">
              <h3 className="text-lg text-foreground font-medium mb-4">
                Eventos em {MESES[currentMonth]}
              </h3>
              {eventosLista.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum evento programado para este mês.</p>
              ) : (
                <div className="space-y-4">
                  {eventosLista.map((event) => (
                    <div key={event.id.value} className="p-5 bg-card rounded-lg border hover:shadow-md transition-shadow">
                      <h4 className="text-base text-primary mb-1">{event.title.value}</h4>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-2">
                        <span className="flex items-center gap-1.5">
                          <CalendarDays size={14} className="text-accent" />
                          {event.date.value.getDate()} de {MESES[event.date.value.getMonth()].toLowerCase()}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock size={14} className="text-accent" />
                          {event.time.value}{event.endTime ? ` – ${event.endTime.value}` : ''}
                        </span>
                      </div>

                      {event.subtitle.value && (
                        <p className="text-sm text-muted-foreground mb-2">{event.subtitle.value}</p>
                      )}

                      <Link to="/contato" className="text-sm font-medium text-primary hover:underline">
                        Saber mais →
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
